import React, { useState } from 'react';

export interface AccordionItemProps {
  id: string;
  title: string;
  content: React.ReactNode;
}

export interface AccordionProps {
  items: AccordionItemProps[];
  /** ID of the item to open by default (default: first item) */
  defaultOpenId?: string;
  /** Allow multiple items open at once (default: false) */
  allowMultiple?: boolean;
  className?: string;
}

const Accordion: React.FC<AccordionProps> = ({
  items,
  defaultOpenId,
  allowMultiple = false,
  className = '',
}) => {
  const defaultOpen = defaultOpenId ?? items[0]?.id ?? null;
  const [openIds, setOpenIds] = useState<Set<string>>(
    () => new Set(defaultOpen ? [defaultOpen] : [])
  );

  const toggle = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (!allowMultiple) next.clear();
        next.add(id);
      }
      return next;
    });
  };

  if (items.length === 0) return null;

  return (
    <div className={`accordion ${className}`} id="accordion-reusable">
      {items.map((item) => {
        const isOpen = openIds.has(item.id);
        const collapseId = `accordion-${item.id}`;
        return (
          <div key={item.id} className="accordion-item">
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${isOpen ? '' : 'collapsed'}`}
                type="button"
                aria-expanded={isOpen}
                aria-controls={collapseId}
                onClick={() => toggle(item.id)}
              >
                {item.title}
              </button>
            </h2>
            <div
              id={collapseId}
              className={`accordion-collapse collapse ${isOpen ? 'show' : ''}`}
            >
              <div className="accordion-body">{item.content}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;
